import { useMutation, useQueryClient } from 'react-query';

import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import { GET_PROPERTIES_PATH } from 'src/services/api/properties/constants';
import type { IPropertyFact } from 'src/services/api/properties/types';
import type { PropertyFactKey } from 'src/services/api/properties/property-fact-keys';

export function usePropertyFactMutations(propertyId: number) {
    const queryClient = useQueryClient();
    const invalidate = () => queryClient.invalidateQueries(queryKeys.properties);

    const create = useMutation<IPropertyFact, Error, { key: PropertyFactKey; value: string }>(
        async ({ key, value }) => {
            const path = `${GET_PROPERTIES_PATH}/${propertyId}/facts`;
            return apiClient(path, { method: 'post', json: { key, value } }).json<IPropertyFact>();
        },
        { onSuccess: invalidate }
    );

    const update = useMutation<IPropertyFact, Error, { factId: number; value: string }>(
        async ({ factId, value }) => {
            const path = `${GET_PROPERTIES_PATH}/${propertyId}/facts/${factId}`;
            return apiClient(path, { method: 'patch', json: { value } }).json<IPropertyFact>();
        },
        { onSuccess: invalidate }
    );

    const remove = useMutation<void, Error, { factId: number }>(
        async ({ factId }) => {
            const path = `${GET_PROPERTIES_PATH}/${propertyId}/facts/${factId}`;
            await apiClient(path, { method: 'delete' });
        },
        { onSuccess: invalidate }
    );

    return { create, update, remove };
}
